import { Channel, User } from 'src/store/models/index.js'
import Channels from 'src/api/channels.js'
import {waitFor} from 'src/config/setup/plugins/waitFor/index.js'

const calendarMixin = {
  data: () => ({
    chatId: null,
    currentAbortController: null,
  }),
  computed: {
    isAllDaySameDay () {
      // all day event on a single day
      if (!this.localEvent) return
      return this.localEvent.is_all_day && this.$moment(this.localEvent.begins_at).isSame(this.localEvent.ends_at, 'day')
    },

    eventsHaveBeenChanged () {
      return this.$store.getters['calendar/REFETCH_CALENDAR_VIEW']
    }
  },
  methods: {
    navigateToCalendar () {
      if (!this.$me?.id) return

      const viewType = window.localStorage?.getItem('calendar_view_type')
      this.$router.push(viewType === 'list'
        ? { name: 'calendar-list', params: { id: this.$me?.id } }
        : { name: 'calendar-grid', params: { id: this.$me?.id }, query: { viewType } })
    },

    getFormattedDateCaptions (event, isEventCard) {
      if (!event) return

      const eventBeginsAt = isEventCard && event.isMultiDay ? event.initialBeginsAt : event.begins_at
      const eventEndsAt = isEventCard && event.isMultiDay ? event.initialEndsAt : event.ends_at

      const localisedBeginsAtMoment = (typeof eventBeginsAt === 'number') ? this.$moment.unix(eventBeginsAt) : this.$moment(eventBeginsAt)
      const localisedEndsAtMoment = (typeof eventEndsAt === 'number') ? this.$moment.unix(eventEndsAt) : this.$moment(eventEndsAt)

      const formattedBeginsAt = isEventCard ? this.$dateFormat.date_dayshort_monthshort(localisedBeginsAtMoment) : this.$dateFormat.date_daylong_dayshort_monthlong_yearlong(localisedBeginsAtMoment)
      const formattedEndsAt = isEventCard ? this.$dateFormat.date_dayshort_monthshort(localisedEndsAtMoment) : this.$dateFormat.date_daylong_dayshort_monthlong_yearlong(localisedEndsAtMoment)

      // event is set to all day
      // 07.03.2022 is_all_day => {caption0: "Montag, 07. März 2022", caption1: "Ganztägig"}
      // TODO Remove "isAllDaySameDay" as soon as events all use dateRange API
      if ((event.isAllDay && !event.isMultiDay) || this.isAllDaySameDay) {
        return {
          caption0: { caption: formattedBeginsAt, dataCy: '' },
          caption1: { caption: this.$t('calendar.list.event.is_fullday'), dataCy: '-start-full-day' }
        }
      }

      const beginsAt = this.getDateTime(localisedBeginsAtMoment)
      const endsAt = this.getDateTime(localisedEndsAtMoment)
      const dateChanged = beginsAt.date !== endsAt.date
      const beginsAtTimeIsFullDay = beginsAt.time === '00:00:00'
      const endsAtTimeIsFullDay = endsAt.time === '23:59:59'

      const beginsAtTime = beginsAtTimeIsFullDay ? this.$t('calendar.list.event.start_of_day') : this.$dateFormat.hour_minute(beginsAt.dateWithTime)
      const endsAtTime = endsAtTimeIsFullDay ? this.$t('calendar.list.event.end_of_day') : this.$dateFormat.hour_minute(endsAt.dateWithTime)
      const timeChanged = beginsAt.time !== endsAt.time

      // ONLY time is different
      // 07.03.2022 10:00 - 13:00 => {caption0: "Montag, 07. März 2022", caption1: "10:00 - 13:00"}
      if (!dateChanged) {
        return {
          caption0: {
            caption: isEventCard ? beginsAtTime : formattedBeginsAt,
            dataCy: beginsAtTimeIsFullDay ? '-start-full-day' : ''
          },
          caption1: {
            caption: isEventCard ? endsAtTime : `${beginsAtTime} - ${endsAtTime}`,
            dataCy: `${beginsAtTimeIsFullDay ? '-start-full-day' : ''}${endsAtTimeIsFullDay ? '-end-full-day' : ''}`
          }
        }
      }

      // BOTH are different
      // 07.03.2022 10:00 - 09.03.2022 20:00 => {caption0: "Montag, 07. März 2022 10:00", caption1: "bis", caption2: "Mittwoch, 09. März 2022 20:00"}
      // 07.03.2022 is_all_day - 09.03.2022 20:00 => {caption0: "Montag, 07. März 2022 (ganztägig)", caption1: "bis", caption2: "Mittwoch, 09. März 2022 20:00"}
      if (timeChanged) {
        // all day event which spans over more than one day
        const beginsAtEventCardAllDay = event.is_all_day || event.isAllDay ? formattedBeginsAt : `${formattedBeginsAt} ${beginsAtTime}`
        const endsAtEventCardAllDay = event.is_all_day || event.isAllDay ? formattedEndsAt : `${formattedEndsAt} ${endsAtTime}`

        return {
          caption0: {
            caption: beginsAtEventCardAllDay,
            dataCy: beginsAtTimeIsFullDay ? '-start-full-day' : ''
          },
          caption1: {
            caption: isEventCard ? endsAtEventCardAllDay : `${this.$t('calendar.event_details.until')}`,
            dataCy: isEventCard ? endsAtTimeIsFullDay ? '-end-full-day' : '' : '-until'
          },
          caption2: {
            caption: endsAtEventCardAllDay,
            dataCy: endsAtTimeIsFullDay ? '-end-full-day' : ''
          }
        }
      }

      // ONLY date is different
      // 07.03.2022 is_all_day - 09.03.2022 is_all_day => {caption0: "Montag, 07. März 2022", caption1: "bis", caption2: "Mittwoch, 09. März 2022"}
      return {
        caption0: {
          caption: formattedBeginsAt,
          dataCy: ''
        },
        caption1: {
          caption: isEventCard ? formattedEndsAt : this.$t('calendar.event_details.until'),
          dataCy: isEventCard ? '' : '-until'
        },
        caption2: {
          caption: formattedEndsAt,
          dataCy: ''
        }
      }
    },
    getChannelParticipants (event) {
      return event.channels?.filter(channel => ['GENERATED', 'CUSTOM', 'GLOBAL'].includes(channel.type))
        .map((channel) => new Channel(channel))
    },
    getUserParticipants (event) {
      return event.channels?.filter(channel => ['CHAT'].includes(channel.type))
        .map((channel) => new User(channel))
    },

    transformUserChannelParticipants (event) {
      // Tech debt: since BE returns all participants within channel (for now), but requires the usage of channel + users
      // when submitting data, we need to transform them back. This can probably be removed when calendar API only works
      // with channels.
      const channelInvitees = [...this.getChannelParticipants(event)]
      const userInvitees = this.getUserParticipants(event).map(channelUser => {
        return {
          ...channelUser,
          id: channelUser.meta.user_id,
          type: channelUser.meta.user_type
        }
      })
      return { ...event, users: userInvitees, channels: channelInvitees, is_notifiable: true }
    },

    fetchEventById: waitFor('fetch-event', async function (eventId) {
      this.currentAbortController = new AbortController()
      const { data } = await Channels.getCalendarSingleEvent(eventId, {signal: this.currentAbortController.signal})
      this.currentAbortController = null
      this.localEvent = this.$clone(data)
    }),
    fetchPersonalNotificationChannel: waitFor('fetching-personal-notification-channel', async function () {
      // if we already have chatId in $store, we don't need to fetch it again
      this.chatId = this.$store.getters['notification/PERSONAL_NOTIFICATION_CHAT_ID']
      if (this.chatId) return

      // call same dispatch method as on AppStart / User login
      await this.$store.dispatch('notification/setPersonalNotificationChannelChatId')
      this.chatId = this.$store.getters['notification/PERSONAL_NOTIFICATION_CHAT_ID']
    }),

    transformData (calendarEventsResp, isList = false) {
      if (!calendarEventsResp || !Object.keys(calendarEventsResp).length) return []

      const applyMinDuration = (beginsAtUnix, endsAtUnix) => {
        // Minimum duration of a calendar event is 15 minutes. If the event is shorter, extend it to 15 minutes.
        const minDuration = 15 * 60

        // longer than 15 minutes
        if (beginsAtUnix + minDuration <= endsAtUnix || isList) return endsAtUnix

        // shorter than 15 minutes
        return beginsAtUnix + minDuration
      }

      // if the event starts at 00:00 and ends at 23:59, it's an all-day event (this can span multiple days)
      const isAllDay = (beginsAt, endsAt) => beginsAt.format('HH:mm:ss') === '00:00:00' && endsAt.format('HH:mm:ss') === '23:59:59'

      // not the same day
      const isMultiDay = (beginsAt, endsAt) => !beginsAt.isSame(endsAt, 'day')

      const getMinutesBeginsAt = (beginsAtMoment) => beginsAtMoment.hour() * 60 + beginsAtMoment.minute()

      const getMinutesEndsAt = (beginsAtMoment, endAtMoment) => {
        const minutesBeginsAt = beginsAtMoment.hour() * 60 + beginsAtMoment.minute()
        const minutesEndsAt = endAtMoment.hour() * 60 + endAtMoment.minute()

        if (isList) return minutesEndsAt

        // also apply min duration of 15 minutes
        if (minutesEndsAt - minutesBeginsAt < 15) return minutesBeginsAt + 15

        // if the event ends at 23:59, we need to return 1440 (24:00)
        if (Number(minutesEndsAt) === 1439) return 1440

        return minutesEndsAt
      }

      const transformToTargetStructure = (calendarEventsResp) => {
        const moment = this.$moment // Reuse moment object (performance gain)

        return calendarEventsResp.map(item => {
          const beginsAtMoment = moment(item.begins_at)
          const endsAtMoment = moment(item.ends_at)

          // calling "local()" on a moment object will transform it to local time
          const localBeginsAtMoment = beginsAtMoment
          const localEndsAtMoment = endsAtMoment

          // Calling ".unix()" will apply local timezone
          const localBeginsAtUnix = beginsAtMoment.unix()
          const localEndsAtUnix = endsAtMoment.unix()

          return {
            ...item,
            begins_at: localBeginsAtUnix,
            ends_at: applyMinDuration(localBeginsAtUnix, localEndsAtUnix),
            isAllDay: isAllDay(localBeginsAtMoment, localEndsAtMoment),
            isMultiDay: isMultiDay(localBeginsAtMoment, localEndsAtMoment),
            week: localBeginsAtMoment.isoWeek(),
            weekday: localBeginsAtMoment.isoWeekday(),
            minutes_begins_at: getMinutesBeginsAt(localBeginsAtMoment),
            minutes_ends_at: getMinutesEndsAt(localBeginsAtMoment, localEndsAtMoment),
            beginsAtMoment: localBeginsAtMoment, // Store moment objects for reuse (performance gain)
            endsAtMoment: localEndsAtMoment
          }
        })
      }

      const distributeMultiDayEvents = (calendarEvents) => {
        return calendarEvents.reduce((acc, item) => {
          // remove unneeded properties again. We only needed them to calculate with optimised performance
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { beginsAtMoment, endsAtMoment, ...restOfItem } = item

          // copy and distribute multi-day events to each day
          if (item.isMultiDay) {
            const beginsAt = item.beginsAtMoment
            const endsAt = item.endsAtMoment

            const daysDiff = Math.ceil(item.endsAtMoment.clone().endOf('day').diff(item.beginsAtMoment.clone().startOf('day'), 'days', true)) - 1
            for (let i = 0; i <= daysDiff; i++) {
              // first and last event need to be adjusted to the actual beginsAt and endsAt
              const newBeginsAt = i === 0 ? beginsAt : beginsAt.clone().add(i, 'days').startOf('day')
              const newEndsAt = i === daysDiff ? endsAt : beginsAt.clone().add(i, 'days').endOf('day')

              const newDaysDiff = newBeginsAt.diff(beginsAtMoment, 'weeks', true) > 0
                ? Math.ceil(endsAt.diff(newBeginsAt, 'days', true))
                : daysDiff + 1

              acc.push({
                ...restOfItem,
                begins_at: newBeginsAt.unix(),
                ends_at: newEndsAt.unix(),
                // we need to keep the original begins_at and ends_at for the date captions
                initialBeginsAt: beginsAt,
                initialEndsAt: endsAt,
                minutes_begins_at: getMinutesBeginsAt(newBeginsAt),
                minutes_ends_at: getMinutesEndsAt(newBeginsAt, newEndsAt),
                week: newBeginsAt.isoWeek(),
                weekday: newBeginsAt.isoWeekday(),
                daysDuration: newDaysDiff
              })
            }
          } else {
            acc.push({ ...restOfItem })
          }
          return acc
        }, [])
      }

      return distributeMultiDayEvents(transformToTargetStructure(calendarEventsResp))
    }
  }
}

export default calendarMixin
